import { useSnapshot } from "valtio";
import type { Analytics } from "@models/analytics.model";
import { clientDashboardEvents } from "../clientDashboardEvents";
import { OPTIONS as DAY_OPTIONS } from "./DaysSelect";

interface Network {
    reporting_network: string;
    network_label: string;
    engagements: number;
    share_pct: number;
    icon?: string;
    color?: string;
    bg_color?: string;
}

interface NetworkStats {
    share: number;
    total: number | null;
    brand: string;
}

// Single source for everything the tile derives from the returned network.
// `total` is reverse-computed from engagements + share until the backend
// returns it directly — swap the bodies here and the markup stays the same.
function getNetworkStats(network: Network): NetworkStats {
    const share = network.share_pct ?? 0;
    const total = network.share_pct
        ? Math.round(network.engagements / (network.share_pct / 100))
        : null;
    return { share, total, brand: network.color || "#845adf" };
}

export default function TopNetwork({ analytics }: { analytics: Analytics }) {
    const snap = useSnapshot(clientDashboardEvents);
    const networks: Network[] = analytics.charts?.["top-network"] ?? [];
    const top = networks[0];
    const stats = top ? getNetworkStats(top) : null;
    const rangeLabel = DAY_OPTIONS.find((o) => o.value === snap.days)?.label;

    return (
        <div className="dash-card">
            <div className="dash-card__head">
                <span className="dash-card__title">Top Performing Network</span>
                {rangeLabel && <span className="dash-card__meta">{rangeLabel}</span>}
            </div>
            <div className="dash-card__body">
                {!top || !stats ? (
                    <div className="dash-empty">No network data available.</div>
                ) : (
                    <div className="network-top">
                        <div className="network-top__id">
                            <div
                                className="network-top__icon"
                                style={{ backgroundColor: top.bg_color, color: top.color }}
                            >
                                <i className={top.icon ?? ""} />
                            </div>
                            <span className="network-top__name">{top.network_label}</span>
                            <span className="network-top__rank" style={{ color: stats.brand }}>#1 network</span>
                        </div>

                        <div className="network-top__share">
                            <div className="network-top__share-head">
                                <span className="network-top__label">Share of total engagements</span>
                                <span className="network-top__share-pct">{top.share_pct ?? 0}%</span>
                            </div>
                            <div className="network-top__bar">
                                <div
                                    className="network-top__bar-fill"
                                    style={{ width: `${stats.share}%`, backgroundColor: stats.brand }}
                                />
                            </div>
                        </div>

                        <div className="network-top__kpis">
                            <div className="network-top__kpi">
                                <span className="network-top__kpi-value">{top.engagements.toLocaleString()}</span>
                                <span className="network-top__kpi-label">Engagements</span>
                            </div>
                            {stats.total != null && (
                                <div className="network-top__kpi network-top__kpi--end">
                                    <span className="network-top__kpi-value">{stats.total.toLocaleString()}</span>
                                    <span className="network-top__kpi-label">Total engagements</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
